import { api } from './baseApi';
import type {
  OrderResult,
  OrderResultResponse,
  PatientMedicalAttachmentResponse,
  PatientMedicalAttachmentsResponse,
  PatientMedicalFileResponse,
} from '../../types/orderResult';

export const orderResultsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrderResult: builder.query<OrderResultResponse, number>({
      query: (orderId) => `/dashboard/orders/${orderId}/result/`,
      providesTags: (_result, _error, orderId) => [{ type: 'OrderResult', id: orderId }],
    }),
    saveOrderResult: builder.mutation<OrderResultResponse, { orderId: number; description: string }>({
      query: ({ orderId, description }) => ({
        url: `/dashboard/orders/${orderId}/result/`,
        method: 'PUT',
        body: { description },
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderResult', id: orderId },
        { type: 'Order', id: orderId },
        'PatientMedicalFile',
      ],
    }),
    uploadOrderResultAttachment: builder.mutation<
      OrderResultResponse,
      { orderId: number; formData: FormData }
    >({
      query: ({ orderId, formData }) => ({
        url: `/dashboard/orders/${orderId}/result/attachments/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderResult', id: orderId },
        { type: 'Order', id: orderId },
        'PatientMedicalFile',
      ],
    }),
    deleteOrderResultAttachment: builder.mutation<
      { success: boolean },
      { orderId: number; attachmentId: number }
    >({
      query: ({ orderId, attachmentId }) => ({
        url: `/dashboard/orders/${orderId}/result/attachments/${attachmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderResult', id: orderId },
        { type: 'Order', id: orderId },
        'PatientMedicalFile',
      ],
    }),
    getPatientMedicalFile: builder.query<
      PatientMedicalFileResponse,
      { patientId: number; page?: number; page_size?: number }
    >({
      query: ({ patientId, page = 1, page_size = 50 }) => ({
        url: `/dashboard/patients/${patientId}/medical-file/`,
        params: { page, page_size },
      }),
      providesTags: (_result, _error, { patientId }) => [{ type: 'PatientMedicalFile', id: patientId }],
    }),
    uploadPatientMedicalAttachment: builder.mutation<
      PatientMedicalAttachmentResponse,
      { patientId: number; formData: FormData }
    >({
      query: ({ patientId, formData }) => ({
        url: `/dashboard/patients/${patientId}/medical-file/attachments/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientMedicalFile', id: patientId },
      ],
    }),
    deletePatientMedicalAttachment: builder.mutation<
      { success: boolean },
      { patientId: number; attachmentId: number }
    >({
      query: ({ patientId, attachmentId }) => ({
        url: `/dashboard/patients/${patientId}/medical-file/attachments/${attachmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientMedicalFile', id: patientId },
      ],
    }),
    getPatientMedicalAttachments: builder.query<
      PatientMedicalAttachmentsResponse,
      number
    >({
      query: (patientId) => `/dashboard/patients/${patientId}/medical-file/attachments/`,
      providesTags: (_result, _error, patientId) => [{ type: 'PatientMedicalFile', id: patientId }],
    }),
  }),
});

export const {
  useGetOrderResultQuery,
  useSaveOrderResultMutation,
  useUploadOrderResultAttachmentMutation,
  useDeleteOrderResultAttachmentMutation,
  useGetPatientMedicalFileQuery,
  useUploadPatientMedicalAttachmentMutation,
  useDeletePatientMedicalAttachmentMutation,
  useGetPatientMedicalAttachmentsQuery,
} = orderResultsApi;

export type { OrderResult };
